/**
 * MQTT Manager for React Frontend
 * Manages multiple MQTT connections using mqtt.js (browser-compatible via WebSocket)
 * 
 * Usage:
 *   import { MQTTManager } from './Utils/mqtt';
 *   
 *   // Create a client
 *   const client = MQTTManager.createClient({
 *     clientId: 'my-client',
 *     host: 'ws://broker.example.com:8083/mqtt', // WebSocket URL
 *     username: 'user',
 *     password: 'pass',
 *   }, (statusUpdate) => console.log(statusUpdate));
 *   
 *   // Subscribe to a topic
 *   await client.subscribe('sensors/+/temperature', (topic, message) => {
 *     console.log(topic, message.toString());
 *   });
 *   
 *   // Publish a message
 *   await client.publish('sensors/room1/command', { action: 'on' });
 *   
 *   // Cleanup
 *   client.close();
 */

import mqtt from 'mqtt';

/**
 * Match a received topic against subscribed topic patterns
 * Supports MQTT wildcards: + (single level) and # (multi level)
 * - '+' matches exactly one topic level
 * - '#' matches zero or more topic levels (must be at end)
 * @param {string} received - The received topic
 * @param {string[]} subscriptions - Array of subscribed topic patterns
 * @returns {string|false} - Matched subscription pattern or false
 */
function findTopic(received, subscriptions) {
  for (const subscribed of subscriptions) {
    let regex = subscribed;
    
    // Step 1: Replace wildcards with placeholders (to avoid escaping issues)
    // Use characters that won't appear in MQTT topics
    // eslint-disable-next-line
    regex = regex.replace(/\+/g, '\u0001');      // + wildcard placeholder
    // eslint-disable-next-line
    regex = regex.replace(/\/#$/, '\u0002');     // /# at end placeholder
    // eslint-disable-next-line
    regex = regex.replace(/^#$/, '\u0003');      // standalone # placeholder
    
    // Step 2: Escape special regex characters
    regex = regex.replace(/\$/g, '\\$');
    regex = regex.replace(/\//g, '\\/');
    
    // Step 3: Replace placeholders with actual regex patterns
    // eslint-disable-next-line
    regex = regex.replace(/\u0001/g, '[^/]+');           // + matches one level
    // eslint-disable-next-line
    regex = regex.replace(/\u0002/g, '(\\/.*)?');        // /# matches zero or more levels
    // eslint-disable-next-line
    regex = regex.replace(/\u0003/g, '.*');              // # alone matches everything
    
    regex = '^' + regex + '$';

    if (received.match(regex) !== null) {
      return subscribed;
    }
  }
  return false;
}

/**
 * MQTT Connection Status Types
 */
export const MQTTStatus = {
  INITIATED: 'initiated',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected',
  OFFLINE: 'offline',
  CLOSED: 'closed',
  ERROR: 'error',
};

/**
 * Single MQTT Client Instance
 */
class MQTTClient {
  constructor(clientId, config, statusCallback) {
    this.clientId = clientId;
    this.host = config.host;
    this.username = config.username;
    this.password = config.password;
    this.reconnectPeriod = config.reconnectPeriod ?? 5000;
    
    this.subscriptions = {};
    this.status = null;
    this.statusCallback = statusCallback || (() => {});
    this.connection = null;

    this._changeStatus(MQTTStatus.INITIATED);
    this._connect();
  }

  /**
   * Establish MQTT connection
   */
  _connect() {
    const options = {
      clientId: this.clientId,
      username: this.username,
      password: this.password,
      reconnectPeriod: this.reconnectPeriod,
      clean: true,
    };

    // For browser, host should be a WebSocket URL (ws:// or wss://)
    // Example: ws://broker.example.com:8083/mqtt or wss://broker.example.com:8084/mqtt
    this.connection = mqtt.connect(this.host, options);

    // Connection events
    this.connection.on('connect', () => {
      this._changeStatus(MQTTStatus.CONNECTED);
      // Re-subscribe to all topics after reconnection
      this._resubscribeAll();
    });

    this.connection.on('error', (err) => {
      this._changeStatus(MQTTStatus.ERROR);
      // console.error(`[MQTT] ${this.clientId} error:`, err);
      this.statusCallback({
        type: 'error',
        clientId: this.clientId,
        error: err.message,
        summary: { fill: 'red', shape: 'dot', text: err.message },
      });
    });

    this.connection.on('reconnect', () => {
      this._changeStatus(MQTTStatus.RECONNECTING);
    });

    this.connection.on('disconnect', () => {
      this._changeStatus(MQTTStatus.DISCONNECTED);
    });

    this.connection.on('offline', () => {
      this._changeStatus(MQTTStatus.OFFLINE);
    });

    this.connection.on('close', () => {
      this._changeStatus(MQTTStatus.CLOSED);
    });

    this.connection.on('message', (topic, message) => {
      const subTopic = findTopic(topic, Object.keys(this.subscriptions));
      if (subTopic && this.subscriptions[subTopic]) {
        try {
          this.subscriptions[subTopic](topic, message);
        } catch (err) {
          // console.error(`[MQTT] ${this.clientId} callback error on ${topic}:`, err);
        }
      } else {
        // console.warn(`[MQTT] ${this.clientId} received message without subscription on ${topic}`);
      }
    });
  }

  /**
   * Re-subscribe to all topics (used after reconnection)
   */
  async _resubscribeAll() {
    const topics = Object.keys(this.subscriptions);
    for (const topic of topics) {
      try {
        await this.connection.subscribeAsync(topic);
        // console.info(`[MQTT] ${this.clientId} re-subscribed to: ${topic}`);
      } catch (err) {
        // console.error(`[MQTT] ${this.clientId} re-subscribe error on ${topic}:`, err);
      }
    }
  }

  /**
   * Update and notify status change
   */
  _changeStatus(status) {
    const lastStatus = this.status;
    this.status = status;

    // console.debug(`[MQTT] ${this.clientId}: ${lastStatus} -> ${status}`);

    this.statusCallback({
      type: 'status_change',
      clientId: this.clientId,
      lastStatus,
      status,
      subscriptions: Object.keys(this.subscriptions),
      summary: this._getSummary(),
    });
  }

  /**
   * Get status summary for UI display
   */
  _getSummary() {
    const statusConfig = {
      [MQTTStatus.CONNECTED]: { fill: 'green', shape: 'dot', text: 'Connected' },
      [MQTTStatus.RECONNECTING]: { fill: 'yellow', shape: 'ring', text: 'Reconnecting...' },
      [MQTTStatus.DISCONNECTED]: { fill: 'red', shape: 'dot', text: 'Disconnected' },
      [MQTTStatus.OFFLINE]: { fill: 'red', shape: 'ring', text: 'Offline' },
      [MQTTStatus.CLOSED]: { fill: 'gray', shape: 'ring', text: 'Closed' },
      [MQTTStatus.ERROR]: { fill: 'red', shape: 'dot', text: 'Error' },
      [MQTTStatus.INITIATED]: { fill: 'blue', shape: 'ring', text: 'Connecting...' },
    };
    return statusConfig[this.status] || { fill: 'gray', shape: 'ring', text: this.status };
  }

  /**
   * Check if client is connected
   */
  isConnected() {
    return this.status === MQTTStatus.CONNECTED;
  }

  /**
   * Subscribe to a topic
   * @param {string} topic - Topic pattern (supports + and # wildcards)
   * @param {Function} callback - Callback function(topic, message)
   */
  async subscribe(topic, callback) {
    const existingTopic = findTopic(topic, Object.keys(this.subscriptions));
    if (existingTopic) {
      // console.info(`[MQTT] ${this.clientId} topic already subscribed: ${existingTopic}`);
      return false;
    }

    try {
      this.subscriptions[topic] = callback;
      const granted = await this.connection.subscribeAsync(topic);// eslint-disable-line
      // console.info(`[MQTT] ${this.clientId} subscribed to: ${granted[0]?.topic || topic}`);
      this._changeStatus(this.status); // Update to notify subscription change
      return true;
    } catch (err) {
      // console.error(`[MQTT] ${this.clientId} subscribe error:`, err);
      delete this.subscriptions[topic];
      return false;
    }
  }

  /**
   * Unsubscribe from a topic
   * @param {string} topic - Topic pattern to unsubscribe
   */
  async unsubscribe(topic) {
    const subTopic = findTopic(topic, Object.keys(this.subscriptions));
    if (!subTopic) {
      // console.warn(`[MQTT] ${this.clientId} topic not subscribed: ${topic}`);
      return false;
    }

    try {
      await this.connection.unsubscribeAsync(topic);
      delete this.subscriptions[topic];
      // console.info(`[MQTT] ${this.clientId} unsubscribed from: ${topic}`);
      this._changeStatus(this.status); // Update to notify subscription change
      return true;
    } catch (err) {
      // console.error(`[MQTT] ${this.clientId} unsubscribe error:`, err);
      return false;
    }
  }

  /**
   * Publish a message
   * @param {string} topic - Topic to publish to
   * @param {string|object} payload - Message payload (objects will be JSON stringified)
   * @param {object} options - MQTT publish options (qos, retain, etc.)
   */
  async publish(topic, payload, options = {}) {
    if (!this.isConnected()) {
      // console.warn(`[MQTT] ${this.clientId} not connected, cannot publish to ${topic}`);
      return false;
    }

    try {
      const message = typeof payload === 'string' ? payload : JSON.stringify(payload);
      await this.connection.publishAsync(topic, message, options);
      return true;
    } catch (err) {
      // console.error(`[MQTT] ${this.clientId} publish error:`, err);
      return false;
    }
  }

  /**
   * Close the connection
   */
  close() {
    if (this.connection) {
      this.connection.end();
      this.connection = null;
    }
    this.subscriptions = {};
    this._changeStatus(MQTTStatus.CLOSED);
  }

  /**
   * Get current subscriptions
   */
  getSubscriptions() {
    return Object.keys(this.subscriptions);
  }
}

/**
 * MQTT Manager - Singleton to manage multiple MQTT connections
 */
class MQTTManagerClass {
  constructor() {
    this.clients = {};
    this.statusListeners = new Set();
  }

  /**
   * Add a global status listener
   * @param {Function} listener - Callback function for status updates
   * @returns {Function} - Unsubscribe function
   */
  addStatusListener(listener) {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /**
   * Notify all global status listeners
   */
  _notifyListeners(update) {
    this.statusListeners.forEach((listener) => {
      try {
        listener(update);
      } catch (err) {
        // console.error('[MQTT Manager] Status listener error:', err);
      }
    });
  }

  /**
   * Create or get existing MQTT client
   * @param {object} config - Connection configuration
   * @param {string} config.clientId - Unique client identifier
   * @param {string} config.host - WebSocket URL (ws:// or wss://)
   * @param {string} [config.username] - Username for authentication
   * @param {string} [config.password] - Password for authentication
   * @param {number} [config.reconnectPeriod=5000] - Reconnect interval in ms
   * @param {Function} [statusCallback] - Optional status callback for this client
   * @returns {MQTTClient} - MQTT client instance
   */
  createClient(config, statusCallback) {
    const clientId = config.clientId;

    // Return existing connected client
    const existing = this.getConnectedClient(clientId);
    if (existing) {
      // console.info(`[MQTT Manager] Client already connected: ${clientId}`);
      return existing;
    }

    // Close existing non-connected client if any
    if (this.clients[clientId]) {
      this.clients[clientId].close();
    }

    // console.info(`[MQTT Manager] Creating new client: ${clientId}`);

    // Create combined status callback
    const combinedCallback = (update) => {
      // Notify specific callback
      if (statusCallback) statusCallback(update);
      // Notify global listeners
      this._notifyListeners({ ...update, clients: this.listClients() });
    };

    this.clients[clientId] = new MQTTClient(clientId, config, combinedCallback);
    return this.clients[clientId];
  }

  /**
   * Get client by ID (any status)
   * @param {string} clientId - Client identifier
   * @returns {MQTTClient|null}
   */
  getClient(clientId) {
    return this.clients[clientId] || null;
  }

  /**
   * Get connected client by ID
   * @param {string} clientId - Client identifier
   * @returns {MQTTClient|null}
   */
  getConnectedClient(clientId) {
    const client = this.getClient(clientId);
    return client?.isConnected() ? client : null;
  }

  /**
   * Close a specific client
   * @param {string} clientId - Client identifier
   * @returns {boolean} - Whether client was closed
   */
  closeClient(clientId) {
    const client = this.getClient(clientId);
    if (client) {
      client.close();
      delete this.clients[clientId];
      this._notifyListeners({ type: 'client_closed', clientId, clients: this.listClients() });
      return true;
    }
    return false;
  }

  /**
   * Close all clients
   */
  closeAll() {
    Object.keys(this.clients).forEach((clientId) => {
      this.clients[clientId].close();
    });
    this.clients = {};
    this._notifyListeners({ type: 'all_closed', clients: {} });
  }

  /**
   * List all clients with their status
   * @returns {object} - Map of clientId to status info
   */
  listClients() {
    const result = {};
    Object.entries(this.clients).forEach(([id, client]) => {
      result[id] = {
        status: client.status,
        subscriptions: client.getSubscriptions(),
      };
    });
    return result;
  }

  /**
   * Get statistics summary
   * @returns {object} - Count of clients by status
   */
  getStats() {
    const stats = {};
    Object.values(this.clients).forEach((client) => {
      stats[client.status] = (stats[client.status] || 0) + 1;
    });
    return stats;
  }
}

// Export singleton instance
export const MQTTManager = new MQTTManagerClass();

// Export class for advanced usage
export { MQTTClient, findTopic };

export default MQTTManager;


